import React, { useEffect, useState, useContext } from 'react';
import propTypes from 'prop-types';
import dynamic from 'next/dynamic';

import styles from './redeem.module.scss';

import reportAmplitudeEvent from '@helpers/analytics';
import { WhiteBox } from '@components/white-box/whitebox';
import { useTranslation } from 'next-i18next';
import { isBrowser } from '@helpers/helper';
import { userContext } from '@components/auth/auth.context';
import { ANGHAMI_PLUS_BENEFITS } from '@enums/utils-constants';
import { setTimeout } from 'timers';

interface RedeemProps {
	promocode: string;
	autoload: number;
	isMobile: boolean;
	isDarkMode: boolean;
	urlParams?: any;
}

const AMPLITUDE_EVENTS = {
	ANON_USER: 'redeem - anon user'
};

const RedeemInputBox = dynamic(async () => import('../redeem-input-box'));
const RedeemCard = dynamic(async () => import('../redeem-card'));
const GiftBox = dynamic(async () => import('../gift-box'));
const LoginBox = dynamic(async () => import('../login-box/login-box'));
const Benefits = dynamic(async () => import('../generic-benefits/index'));

const Redeem = ({ promocode, autoload, isMobile, isDarkMode, urlParams }: RedeemProps) => {
	const { user } = useContext(userContext);
	const { t } = useTranslation();
	const [renderedComponent, setRenderedComponent] = useState(null);
	const [shouldHideHeader, setShouldHideHeader] = useState(autoload || false);

	useEffect(() => {
		if ((user || isMobile) && isBrowser()) {
			setRenderedComponent(renderInitialRedeem);
		} else {
			const _props = {
				utm_source: urlParams.utm_source || 'organic',
				utm_medium: urlParams?.utm_medium,
				utm_campaign: urlParams?.utm_campaign
			};

			setTimeout(() => {
				reportAmplitudeEvent(AMPLITUDE_EVENTS.ANON_USER, _props);
			}, 100);
			setRenderedComponent(renderLoginBox);
		}
	}, [user]);

	const giftBoxComponent = () => {
		return (
			<div className={`gift-wrap-margin ${styles['gift-wrap-margin']}`}>
				<GiftBox />
			</div>
		);
	};

	const renderExtras = () => {
		return <>{giftBoxComponent()}</>;
	};

	const renderLoginBox = () => {
		if (isBrowser()) {
			setShouldHideHeader(false);
			return (
				<>
					<WhiteBox
						customStyle={{
							marginTop: '-5rem'
						}}
					>
						<LoginBox title="login_to_continue" cta="login" classes="green" />
					</WhiteBox>
					{renderExtras()}
				</>
			);
		}

		return null;
	};

	const renderMobileCard = (_data) => {
		if (isBrowser()) {
			setShouldHideHeader(true);
			return (
				<>
					<RedeemCard data={_data} isDarkMode={isDarkMode} urlParams={urlParams} />
					<Benefits
						showDescription={false}
						type="square"
						subtitle={t('redeem_plus_features_title')}
						features={ANGHAMI_PLUS_BENEFITS}
						color="#4EE5B1"
						imgSize={40}
					/>
				</>
			);
		}

		return null;
	};

	const reEvaluateComponent = (_data) => {
		if (_data?.error) {
			setShouldHideHeader(false);
		} else {
			setRenderedComponent(renderMobileCard(_data));
		}
	};

	const renderInitialRedeem = () => {
		return (
			<>
				<RedeemInputBox
					promocode={promocode}
					isMobile={isMobile}
					isDarkMode={isDarkMode}
					isLoggedIn={Boolean(user)}
					urlParams={urlParams}
					onSubmitResponse={(_e) => {
						reEvaluateComponent(_e);
					}}
				/>
				{!autoload ? renderExtras() : null}
			</>
		);
	};

	const renderHeader = () => {
		return (
			<div
				className={`${styles['redeem-header']} flexbox ${
					isMobile ? `start ${styles.mobile}` : 'centered'
				}`}
			>
				<div className={`${styles['redeem-header-title']} bold`}>{t('redeem_header_title')}</div>
			</div>
		);
	};

	return (
		<>
			{shouldHideHeader ? null : renderHeader()}
			<div className={styles.RedeemWrapper}>{renderedComponent}</div>
		</>
	);
};

Redeem.propTypes = {
	promocode: propTypes.string,
	isMobile: propTypes.bool
};

export default Redeem;
